/**
 * #427 W2 — "Ask for a day off" form.
 *
 * Every role holds `time-off:request:self`, so this component is the one
 * request surface for the whole building: it is embedded on /my-schedule
 * (where a groomer or kennel tech will actually look for it) and behind a
 * disclosure on /schedule (where the desk roles live).
 *
 * Design Philosophy principles served:
 *   P2 (the next action is obvious) — the form states the outcome before
 *     you submit ("A manager reviews it…"), and on success says what
 *     happened rather than vanishing into a toast.
 *   P5 (never lie about data) — a submitted request renders as PENDING,
 *     never as "approved" or "done".
 *   P7 (built for hands) — ≥44px controls, native date inputs (the phone's
 *     own date wheel), no hover-only affordances.
 *
 * `primary` lets the host page decide whether Submit is THE filled-primary
 * of the view (true on /my-schedule, which has no competing action) or a
 * demoted outline button (false on /schedule, whose primary is "Add
 * shift"). One filled-primary per view is an auto-reject rule, so the
 * decision belongs to the page, not to this component.
 */
import { useState } from 'react';
import {
  ApiError,
  requestTimeOff,
  readSession,
  TIME_OFF_TYPES,
  TIME_OFF_TYPE_LABELS,
  todayIsoDate,
  type TimeOffRequest,
  type TimeOffType,
} from '../../lib/api.js';

export type TimeOffRequestFormProps = {
  readonly apiUrl: string;
  /** True when Submit may be the view's single filled-primary button. */
  readonly primary?: boolean;
  /** Called with the created (pending) request so the host can prepend it. */
  readonly onSubmitted?: (request: TimeOffRequest) => void;
  readonly onCancel?: () => void;
};

export function TimeOffRequestForm({
  apiUrl,
  primary = false,
  onSubmitted,
  onCancel,
}: TimeOffRequestFormProps): JSX.Element {
  const [startDate, setStartDate] = useState<string>(() => todayIsoDate());
  const [endDate, setEndDate] = useState<string>(() => todayIsoDate());
  const [type, setType] = useState<TimeOffType>('pto');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    // A one-day request is start === end (inclusive range). End-before-start
    // is caught here so the staffer gets a sentence, not a 400.
    if (endDate < startDate) {
      setError('The last day can’t come before the first day.');
      return;
    }
    const { token } = readSession();
    if (!token) {
      setError('Your session expired. Sign in again to send this request.');
      return;
    }
    setSubmitting(true);
    try {
      const { request } = await requestTimeOff(apiUrl, token, {
        startDate,
        endDate,
        type,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      setNote('');
      setSuccess(
        'Request sent. It stays pending until a manager approves or denies it.',
      );
      onSubmitted?.(request);
    } catch (err) {
      setError(
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'We couldn’t send that request. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="ferro-form ferro-timeoff-form"
      onSubmit={handleSubmit}
      aria-label="Request time off"
      data-testid="time-off-form"
    >
      <p className="ferro-timeoff-form__hint">
        Pick the first and last day you’ll be away. A manager reviews it — you
        stay on the schedule until it’s approved.
      </p>

      <div className="ferro-form__row">
        <label className="ferro-field">
          <span className="ferro-field__label">First day away</span>
          <input
            type="date"
            className="ferro-field__input"
            value={startDate}
            onChange={(e) => {
              const next = e.target.value;
              setStartDate(next);
              // Keep the range coherent: a single-day request is the common
              // case, so the end day follows the start until it's set apart.
              if (next && endDate < next) setEndDate(next);
            }}
            required
            data-testid="time-off-start"
          />
        </label>
        <label className="ferro-field">
          <span className="ferro-field__label">Last day away</span>
          <input
            type="date"
            className="ferro-field__input"
            value={endDate}
            min={startDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
            data-testid="time-off-end"
          />
        </label>
      </div>

      <label className="ferro-field">
        <span className="ferro-field__label">Kind of time off</span>
        <select
          className="ferro-field__input"
          value={type}
          onChange={(e) => setType(e.target.value as TimeOffType)}
          data-testid="time-off-type"
        >
          {TIME_OFF_TYPES.map((t) => (
            <option key={t} value={t}>
              {TIME_OFF_TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </label>

      <label className="ferro-field">
        <span className="ferro-field__label">Note (optional)</span>
        <textarea
          className="ferro-field__input ferro-field__input--textarea"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          placeholder="Anything your manager should know"
          data-testid="time-off-note"
        />
      </label>

      {error ? (
        <div className="ferro-alert ferro-alert--error" role="alert">
          {error}
        </div>
      ) : null}
      {success ? (
        <div
          className="ferro-alert ferro-alert--success"
          role="status"
          data-testid="time-off-success"
        >
          {success}
        </div>
      ) : null}

      <div className="ferro-form__actions">
        <button
          type="submit"
          className={`ferro-btn ${primary ? 'ferro-btn--primary' : 'ferro-btn--outline'}`}
          disabled={submitting}
          aria-busy={submitting}
          data-testid="time-off-submit"
        >
          {submitting ? 'Sending…' : 'Send request'}
        </button>
        {onCancel ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}
